import { Button } from '@/components/ui/button';
import { getWaChatContactDisplayName } from '@/common/utils/display-name-utils';
import {
    backendApi,
    MyBridgeAgent,
} from '@common-reducers/backend-api/backend-api';
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { btLayoutSidebarActions } from '@common-reducers/BtLayoutSidebarReducer';
import { namespace } from '@common-config/constants';
import { agentAppUrl, AgentWorkspaceDialog } from '@/pages/my-bridge/AgentWorkspaceDialog';
import { NewAgentButton } from '@common-components/mascot/NewAgentButton';
import { AgentRow } from '@/pages/my-bridge/agent-inspect-shared';
import { useAgentRowActions } from '@/pages/my-bridge/useAgentRowActions';
import { Loader2 } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { sortAgentsForList } from '@/pages/my-bridge/agent-list-order';
import { useAgentPageViewed } from '@common/services/use-agent-page-viewed';
import { agentCoversChat } from '@/pages/my-bridge/agent-chat-coverage';

/**
 * Quick Agents (in WhatsApp Web) — ONE system with /agents: the SAME agent
 * workspace (builder chat + agent form, in a dialog) builds and edits an agent,
 * and the SAME agent rows (`AgentRow`: a row opens the agent, one on/off
 * control, conversations + delete-with-confirm in the `⋯` menu) list them.
 *
 * The ONE difference from /my-bridge: when the panel is scoped to the CURRENT
 * chat, the builder is told the agent serves only this chat's number (auto), so
 * the operator just describes the behaviour. Scope 'all' builds a general agent.
 */

type QuickAgentScope = 'current' | 'all';

const digitsOf = (id: string): string => (id || '').replace(/[^\d]/g, '');

const QuickAgentsSidebar: React.FC = () => {
    const { t, i18n } = useTranslation(namespace);
    const dispatch = useAppDispatch();
    // The in-chat Quick Agents panel genuinely opened (it is conditionally mounted,
    // and every opener is a user action) — a durable, owner-stamped signal, once per
    // open. NOT the passive /my-bridge/overview poll (see useAgentPageViewed).
    useAgentPageViewed('extension_inchat');

    const whatsappCurrentChat = useAppSelector((state) => state.WhatsAppReducer.currentChat);
    const { quickAgentsInitialScope } = useAppSelector((state) => state.BtLayoutSidebarReducer);
    const teamInboxChatViewModeId = useAppSelector((state) => state.TeamInboxReducer?.chatViewMode?.chatId ?? '');

    const isRTL = i18n.dir(i18n.resolvedLanguage || i18n.language) === 'rtl';

    const { data: overview, isLoading, isError, refetch } = backendApi.useGetMyBridgeOverviewQuery();
    const rowActions = useAgentRowActions();

    // Active first, then most recently updated — the same order as the /agents page.
    const agents = useMemo(() => sortAgentsForList(overview?.agents ?? []), [overview]);

    const currentChatId = whatsappCurrentChat?.id?._serialized || '';
    const currentChatDigits = useMemo(() => {
        const set = new Set<string>();
        [currentChatId, teamInboxChatViewModeId].forEach((id) => { const d = digitsOf(id); if (d) set.add(d); });
        return set;
    }, [currentChatId, teamInboxChatViewModeId]);

    const currentChatDisplayName = useMemo(
        () => getWaChatContactDisplayName(whatsappCurrentChat, { fallback: t('teamInbox.quickAgents.unknownChat') }),
        [whatsappCurrentChat, t],
    );

    const [scope, setScope] = useState<QuickAgentScope>(() => (currentChatId ? 'current' : 'all'));
    // The workspace dialog: open on an agent (Edit) or on none (New).
    const [workspaceOpen, setWorkspaceOpen] = useState(false);
    const [workspaceAgent, setWorkspaceAgent] = useState<MyBridgeAgent | null>(null);

    useEffect(() => { if (!currentChatId && scope === 'current') setScope('all'); }, [currentChatId, scope]);
    useEffect(() => {
        if (!quickAgentsInitialScope) return;
        setScope(quickAgentsInitialScope === 'current' && !currentChatId ? 'all' : quickAgentsInitialScope);
        dispatch(btLayoutSidebarActions.clearQuickAgentsInitialScope());
    }, [quickAgentsInitialScope, currentChatId, dispatch]);

    // In 'current' scope, show agents bound to THIS chat (by digits); 'all' shows every agent.
    const filteredAgents = useMemo(() => {
        if (scope === 'all') return agents;
        if (currentChatDigits.size === 0) return [];
        // An agent answering ALL chats (chatScope 'all_except') covers this chat too, not only a listed one (#813).
        const isGroup = [currentChatId, teamInboxChatViewModeId].some((id) => id.endsWith('@g.us'));
        return agents.filter((a) => agentCoversChat(a, currentChatDigits, isGroup));
    }, [scope, agents, currentChatDigits, currentChatId, teamInboxChatViewModeId]);

    const openWorkspace = (agent: MyBridgeAgent | null) => { setWorkspaceAgent(agent); setWorkspaceOpen(true); };

    // rev 3.3 §6.17.1: no projects/connections — a provisioned account can build.
    const provisioned = !!overview?.provisioned;
    const chatContext = scope === 'current' && currentChatId
        ? { chatId: currentChatId, chatName: currentChatDisplayName }
        : undefined;

    return (
        <div className="h-full w-full flex flex-col overflow-hidden" dir={isRTL ? 'rtl' : 'ltr'}>
            <div className="flex-1 min-h-0 p-4 flex flex-col gap-3 overflow-y-auto">
                {/* Scope */}
                <div className="flex items-center gap-2">
                    <Button size="sm" variant={scope === 'current' ? 'secondary' : 'outline'} onClick={() => setScope('current')} disabled={!currentChatId}>
                        {t('teamInbox.quickAgents.currentChat')}
                    </Button>
                    <Button size="sm" variant={scope === 'all' ? 'secondary' : 'outline'} onClick={() => setScope('all')}>
                        {t('teamInbox.quickAgents.allChats')}
                    </Button>
                </div>
                {scope === 'current' && (
                    <p className="text-xs text-muted-foreground truncate" dir="auto">{currentChatDisplayName}</p>
                )}

                {isLoading ? (
                    <div className="py-10 flex items-center justify-center text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin me-2" />{t('teamInbox.quickAgents.loading')}</div>
                ) : isError ? (
                    <p className="text-sm py-4 text-slate-500">{t('myBridge.errorTitle')}{' '}<button className="underline" onClick={() => refetch()}>{t('myBridge.retry')}</button></p>
                ) : !provisioned ? (
                    <div className="rounded-md border p-4 text-sm text-slate-500" style={{ borderColor: 'var(--color-border)' }}>
                        {t('teamInbox.quickAgents.connectBridgeFirst')}
                    </div>
                ) : (
                    <>
                        {/* New Agent — opens the SAME builder as /my-bridge in a
                            spacious popup (no cramped inline card). */}
                        <NewAgentButton
                            label={scope === 'current' ? t('teamInbox.quickAgents.newCurrentChat') : t('teamInbox.quickAgents.newAgent')}
                            onClick={() => openWorkspace(null)}
                            // shrink-0: this column scrolls, and a long agent list would otherwise
                            // shrink the (overflow-hidden) pill down to a sliver.
                            className="w-full shrink-0"
                            data-testid="quick-agents-new-agent"
                        />

                        {/* The agents themselves */}
                        <div className="text-sm font-semibold mt-2">{t('myBridge.agentsTitle')}</div>
                        {filteredAgents.length === 0 ? (
                            <p className="text-sm text-slate-500 py-2">
                                {scope === 'current' ? t('teamInbox.quickAgents.emptyCurrent') : t('teamInbox.quickAgents.emptyAll')}
                            </p>
                        ) : (
                            <div className="flex flex-col divide-y divide-ai-border">
                                {filteredAgents.map((agent) => (
                                    <AgentRow
                                        key={agent.id}
                                        agent={agent}
                                        bridgeId={overview?.bridge?.id}
                                        onOpen={openWorkspace}
                                        openHref={agentAppUrl(agent.id)}
                                        editInNewTab
                                        isOwner
                                        {...rowActions.rowProps(agent)}
                                    />
                                ))}
                            </div>
                        )}
                    </>
                )}
            </div>

            <AgentWorkspaceDialog
                open={workspaceOpen}
                onOpenChange={setWorkspaceOpen}
                agent={workspaceAgent}
                chatContext={chatContext}
                isRtl={isRTL}
            />

            {rowActions.confirmDialog}
        </div>
    );
};

export default QuickAgentsSidebar;
